// src/pages/WorklistStudioPage.jsx
// Self-contained container: manages list / editor / runner views internally
// Pattern mirrors FlowsPage — no React Router navigate() needed.
import { useState, useEffect } from 'react';
import {
    Plus, Search, ClipboardList, MoreHorizontal, Edit2, Trash2,
    Play, Database, Globe, FileText, Clock, LayoutGrid, List
} from 'lucide-react';
import { useAuth } from '@/context/useAuth';
import { interactiveFetch } from '../api';
import { hasPermission } from '../utils/permissions';
import { PERMISSIONS } from '../constants/permissions';
import WorklistEditorPage from './WorklistEditorPage';
import WorklistRunnerPage from './WorklistRunnerPage';
import { notify } from '@/components/core';

const statusConfig = {
    draft: { label: 'Draft', cls: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20', dot: 'bg-amber-500' },
    internal: { label: 'Internal', cls: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-500/20', dot: 'bg-indigo-500' },
    public: { label: 'Public', cls: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20', dot: 'bg-emerald-500' },
};

export default function WorklistStudioPage({ refreshContent }) {
    const [view, setView] = useState('list'); // 'list' | 'editor' | 'runner'
    const [selectedId, setSelectedId] = useState(null);

    useEffect(() => {
        const handleReset = (e) => {
            if (e.detail?.pageId === 'worklist-studio') {
                setView('list');
                setSelectedId(null);
            }
        };
        window.addEventListener('reset-page-state', handleReset);
        return () => window.removeEventListener('reset-page-state', handleReset);
    }, []);

    if (view === 'editor') {
        return (
            <WorklistEditorPage
                isEdit={!!selectedId}
                appId={selectedId}
                onBack={() => { setView('list'); setSelectedId(null); }}
                onSaved={(id) => { setSelectedId(id); setView('runner'); }}
                onDraftSaved={(id) => setSelectedId(id)}
            />
        );
    }
    if (view === 'runner') {
        return (
            <WorklistRunnerPage
                appId={selectedId}
                onBack={() => setView('list')}
                onEdit={(id) => { setSelectedId(id); setView('editor'); }}
            />
        );
    }

    return (
        <WorklistListView
            onNew={() => { setSelectedId(null); setView('editor'); }}
            onEdit={(id) => { setSelectedId(id); setView('editor'); }}
            onOpen={(id) => { setSelectedId(id); setView('runner'); }}
            refreshContent={refreshContent}
        />
    );
}

function WorklistListView({ onNew, onEdit, onOpen, refreshContent }) {
    const { token, user } = useAuth();
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [viewMode, setViewMode] = useState('grid');

    useEffect(() => { if (token) load(); }, [token]);

    const load = async () => {
        setLoading(true);
        try {
            const data = await interactiveFetch('/worklists', token, { method: 'GET' });
            setApps(Array.isArray(data) ? data : []);
        } catch { notify.error('Failed to load Worklist apps'); }
        finally { setLoading(false); }
    };

    const handleDelete = async (app, e) => {
        e.stopPropagation();
        if (!window.confirm(`Delete "${app.name}"?`)) return;
        try {
            await interactiveFetch(`/worklists/${app.id}`, token, { method: 'DELETE' });
            setApps(prev => prev.filter(a => a.id !== app.id));
            notify.success('Deleted');
            window.dispatchEvent(new Event('refresh-sidebar'));
        } catch { notify.error('Delete failed'); }
    };



    const filtered = apps.filter(a =>
        a.name.toLowerCase().includes(search.toLowerCase()) ||
        (a.description || '').toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="flex flex-col h-full bg-gray-50 dark:bg-slate-950 overflow-hidden">
            {/* Header */}
            <div className="flex-none px-6 pt-6 pb-4 border-b border-gray-100 dark:border-slate-800 bg-white dark:bg-slate-900">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl">
                            <ClipboardList className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900 dark:text-slate-100">Worklist Studio</h1>
                            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                                Build configurable human-in-the-loop worklists on your datasources
                            </p>
                        </div>
                    </div>
                    {hasPermission(user, PERMISSIONS.WORKLISTS_CREATE) && (
                        <button id="worklist-create-btn" onClick={onNew}
                            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold shadow-sm transition-all hover:shadow-md">
                            <Plus className="w-4 h-4" /> New Worklist
                        </button>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <div className="relative flex-1 max-w-sm">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input type="text" placeholder="Search worklists…" value={search} onChange={e => setSearch(e.target.value)}
                            className="w-full pl-9 pr-4 py-2 text-sm bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-indigo-200 dark:text-slate-100" />
                    </div>
                    <div className="flex items-center gap-1 bg-gray-100 dark:bg-slate-800 rounded-lg p-1">
                        <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-white dark:bg-slate-700 shadow-sm' : 'text-gray-400'}`}><LayoutGrid className="w-4 h-4" /></button>
                        <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white dark:bg-slate-700 shadow-sm' : 'text-gray-400'}`}><List className="w-4 h-4" /></button>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
                {loading ? (
                    <div className="flex items-center justify-center h-48">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="flex flex-col items-center justify-center text-center p-16 border-2 border-dashed border-gray-200 dark:border-slate-700 rounded-3xl">
                        <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-2xl mb-4">
                            <ClipboardList className="w-8 h-8 text-indigo-400" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-slate-100">No Worklists Yet</h3>
                        <p className="text-sm text-gray-500 dark:text-slate-400 mt-2 max-w-sm">
                            Create your first Worklist Studio app to turn your SQL or SharePoint data into a structured, assignable task queue.
                        </p>
                        {hasPermission(user, PERMISSIONS.WORKLISTS_CREATE) && (
                            <button onClick={onNew} className="mt-6 flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold transition-all shadow-sm">
                                <Plus className="w-4 h-4" /> New Worklist
                            </button>
                        )}
                    </div>
                ) : viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                        {filtered.map(app => (
                            <WorklistCard key={app.id} app={app}
                                onOpen={() => onOpen(app.id)}
                                onEdit={e => { e.stopPropagation(); onEdit(app.id); }}
                                onDelete={e => handleDelete(app, e)}
                            />
                        ))}
                    </div>
                ) : (
                    <WorklistListTable apps={filtered}
                        onOpen={(id) => onOpen(id)}
                        onEdit={(id) => onEdit(id)}
                        onDelete={(app, e) => handleDelete(app, e)}
                    />
                )}
            </div>
        </div>
    );
}

function WorklistCard({ app, onOpen, onEdit, onDelete }) {
    const { user } = useAuth();
    const [menu, setMenu] = useState(false);
    const sc = statusConfig[app.publish_scope] || statusConfig.draft;

    const canEdit = hasPermission(user, PERMISSIONS.WORKLISTS_UPDATE);
    const canPublish = hasPermission(user, PERMISSIONS.WORKLISTS_PUBLISH);
    const canDelete = hasPermission(user, PERMISSIONS.WORKLISTS_DELETE);
    const showMenuButton = canEdit || (canPublish && app.publish_scope === 'draft') || canDelete;

    return (
        <div onClick={onOpen} className="group bg-white/80 dark:bg-slate-900/80 rounded-2xl p-5 border border-gray-100 dark:border-slate-800/80 shadow-[0_8px_30px_rgb(0,0,0,0.02)] hover:shadow-[0_20px_50px_rgba(99,102,241,0.15)] hover:-translate-y-1.5 hover:scale-[1.01] transition-all duration-300 cursor-pointer relative">
            <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-violet-600 via-indigo-600 to-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-t-2xl" />
            <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                    <div className="p-2.5 bg-indigo-50 dark:bg-indigo-950/40 rounded-xl text-indigo-600 dark:text-indigo-400 group-hover:bg-indigo-600 group-hover:text-white group-hover:shadow-[0_0_15px_rgba(99,102,241,0.4)] transition-all duration-300">
                        <ClipboardList className="w-4 h-4" />
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${sc.cls}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${sc.dot} animate-pulse`} />
                        {sc.label}
                    </span>
                </div>
                {showMenuButton && (
                    <div className="relative" onClick={e => e.stopPropagation()}>
                        <button onClick={() => setMenu(v => !v)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors">
                            <MoreHorizontal className="w-4 h-4" />
                        </button>
                        {menu && (
                            <div className="absolute right-0 top-full mt-1 z-50 bg-white dark:bg-slate-900 rounded-xl border border-gray-100 dark:border-slate-800 shadow-xl py-1 min-w-[150px]" onMouseLeave={() => setMenu(false)}>
                                <MI icon={<Play className="w-4 h-4 text-indigo-500" />} label="Open" onClick={onOpen} />
                                {canEdit && <MI icon={<Edit2 className="w-4 h-4 text-gray-400" />} label="Edit Config" onClick={onEdit} />}
                                {canPublish && app.publish_scope === 'draft' && <MI icon={<Sparkles className="w-4 h-4 text-purple-500" />} label="Publish" onClick={onPublish} />}
                                {canDelete && (
                                    <>
                                        <div className="border-t border-gray-100 dark:border-slate-800 my-1" />
                                        <MI icon={<Trash2 className="w-4 h-4 text-red-500" />} label="Delete" onClick={onDelete} danger />
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
            <h3 className="font-bold text-gray-900 dark:text-slate-100 mb-1 line-clamp-1 group-hover:text-indigo-700 dark:group-hover:text-indigo-400 transition-colors">{app.name}</h3>
            <p className="text-xs text-gray-500 dark:text-slate-400 line-clamp-2 min-h-[2.5em]">{app.description || 'No description'}</p>
            <div className="flex items-center gap-3 mt-4 pt-3 border-t border-gray-50 dark:border-slate-800">
                <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-slate-400">
                    {app.source_type === 'sharepoint' ? <Globe className="w-3 h-3" /> : <Database className="w-3 h-3" />}
                    <span>{app.source_type === 'sharepoint' ? 'SharePoint' : 'SQL'}</span>
                </div>
                <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-slate-400">
                    <FileText className="w-3 h-3" /><span>{app.item_count ?? 0} items</span>
                </div>
                {(app.pending_writeback_count || 0) > 0 && (
                    <div className="flex items-center gap-1 text-xs text-amber-600 ml-auto">
                        <Clock className="w-3 h-3 text-amber-500 animate-spin" style={{ animationDuration: '3s' }} /><span>{app.pending_writeback_count} pending</span>
                    </div>
                )}
            </div>
        </div>
    );
}

function MI({ icon, label, onClick, danger }) {
    return (
        <button onClick={onClick} className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors hover:bg-gray-50 dark:hover:bg-slate-800 ${danger ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-slate-200'}`}>
            {icon}{label}
        </button>
    );
}

function WorklistListTable({ apps, onOpen, onEdit, onDelete }) {
    const { user } = useAuth();
    const canEdit = hasPermission(user, PERMISSIONS.WORKLISTS_UPDATE);
    const canDelete = hasPermission(user, PERMISSIONS.WORKLISTS_DELETE);

    return (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-100 dark:border-slate-800 overflow-hidden">
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b border-gray-100 dark:border-slate-800 text-left">
                        {['Name', 'Source', 'Items', 'Status', 'Pending', ''].map(h => (
                            <th key={h} className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {apps.map(app => {
                        const sc = statusConfig[app.publish_scope] || statusConfig.draft;
                        return (
                            <tr key={app.id} onClick={() => onOpen(app.id)} className="border-b border-gray-50 dark:border-slate-800 hover:bg-gray-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors">
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-2">
                                        <ClipboardList className="w-4 h-4 text-indigo-500" />
                                        <p className="font-semibold text-gray-900 dark:text-slate-100">{app.name}</p>
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-gray-500 dark:text-slate-400 capitalize">{app.source_type}</td>
                                <td className="px-4 py-3 text-gray-600 dark:text-slate-300">{app.item_count ?? 0}</td>
                                <td className="px-4 py-3">
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${sc.cls}`}>
                                        <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                                        {sc.label}
                                    </span>
                                </td>
                                <td className="px-4 py-3">{(app.pending_writeback_count || 0) > 0 ? <span className="text-amber-600 text-xs font-medium">{app.pending_writeback_count}</span> : <span className="text-gray-300 text-xs">—</span>}</td>
                                <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                                    <div className="flex items-center gap-1 justify-end">
                                        {canEdit && <button onClick={() => onEdit(app.id)} className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"><Edit2 className="w-4 h-4" /></button>}
                                        {canDelete && <button onClick={e => onDelete(app, e)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"><Trash2 className="w-4 h-4" /></button>}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
